import differenceWith from 'lodash/differenceWith';
import isEqual from 'lodash/isEqual';
import {
  useCloseOutChecklistData,
  useProcessSaveWirResponse,
  useProcessSection,
  useSectionEquipmentIdentificationData,
  useSectionInitialRequestInformationData,
  useSectionManagerialSummaryData,
  useSectionOrganiseKickOffMeetingData,
  useSectionWellInformationData,
} from 'pages/WellGuardian/WIR/hooks';
import {
  getIsUnplannedReactiveScope,
  getKomTechnicalWirReactiveScopeData,
} from 'pages/WellGuardian/WIR/utils';
import {
  Phase,
  WIREngineeringWorkSections,
  WIRKofMeetingTechnicalWirSections,
  WIRReactiveScopeSections,
  WIRStageGate2Sections,
  WIRStageGate3Sections,
} from 'pages/WellGuardian/WIR/wir.constants';
import { useCallback, useState } from 'react';
import { IntlShape } from 'react-intl';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  updateOriginalSelectedWIR,
  updateSelectedSection,
  updateSelectedWIR,
  updateWIRDraftComment,
} from 'redux/WellGuardian';
import {
  useCountryState,
  useOriginalSelectedWIR,
  useSelectedWIR,
} from 'redux/WellGuardian/selectors';
import wellGuardianApiClient from 'services/networking/wellGuardianApiClient';
import { CountryData, WIR, WIRComment } from 'types/wellGuardian';

import {
  getEngineeringWorkPhaseName,
  getEngineeringWorkSectionKey,
  getKickOffPhaseName,
} from '../utils/getSectionData';

export const useSectionUpdate = (section: string, intl: IntlShape, sectionChangeTo?: string) => {
  const [wirUpdateLoading, setWirUpdateLoading] = useState<boolean>(false);
  const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] = useState<boolean>(false);

  const wirFromState = useSelectedWIR();
  const originalWir = useOriginalSelectedWIR();
  const dispatch = useDispatch();
  const { saveInitialRequestInformation } = useSectionInitialRequestInformationData();
  const { saveOrganiseKickOffMeetingSection } = useSectionOrganiseKickOffMeetingData();
  const { saveWellInformationSection } = useSectionWellInformationData();
  const { saveManagerialSummarySection } = useSectionManagerialSummaryData();
  const processSaveWirResponse = useProcessSaveWirResponse();
  const { saveEquipmentIdentificationSection } = useSectionEquipmentIdentificationData();
  const { saveCloseoutChecklistSection } = useCloseOutChecklistData();
  const { processSection } = useProcessSection(section);

  const countryState = useCountryState();

  const cancelSaveChanges = useCallback(() => {
    dispatch(updateSelectedWIR(originalWir));
    sectionChangeTo && dispatch(updateSelectedSection(sectionChangeTo));
    setUnsavedChangesModalOpen(false);
  }, [dispatch, originalWir, sectionChangeTo]);

  const processSaveResponse = useCallback(
    (wirResponse: WIR | null) => {
      if (wirResponse?.wirNumber) {
        dispatch(updateSelectedWIR(wirResponse));
        dispatch(updateOriginalSelectedWIR(wirResponse));
        dispatch(updateWIRDraftComment('')); // Reset any draft comments
        toast.success(
          intl.formatMessage({
            id: 'wellGuardian.wir.wirCreation.wirUpdated',
          }),
        );
        if (sectionChangeTo) {
          dispatch(updateSelectedSection(sectionChangeTo));
          setUnsavedChangesModalOpen(false);
        } else {
          processSection(wirResponse);
        }
      } else {
        toast.error(
          intl.formatMessage({
            id: 'wellGuardian.wir.wirCreation.wirUpdateError',
          }),
        );
      }
    },
    [dispatch, sectionChangeTo, processSection, intl],
  );

  const saveChecklistAndActions = useCallback(
    async (
      section: string,
      phase: string,
      assignedTo: string,
      sectionCompleteConfirmation: boolean | undefined,
      comments: WIRComment[],
    ) => {
      const allChecklistsOriginal = [
        ...(originalWir?.checklist?.generic ?? []),
        ...(originalWir?.checklist?.perforations ?? []),
        ...(originalWir?.checklist?.production_and_reservoir ?? []),
        ...(originalWir?.checklist?.pumping_operation ?? []),
        ...(originalWir?.checklist?.well_stimulation ?? []),
      ];
      const allChecklistsState = [
        ...(wirFromState?.checklist?.generic ?? []),
        ...(wirFromState?.checklist?.perforations ?? []),
        ...(wirFromState?.checklist?.production_and_reservoir ?? []),
        ...(wirFromState?.checklist?.pumping_operation ?? []),
        ...(wirFromState?.checklist?.well_stimulation ?? []),
      ];
      const newChecks = differenceWith(allChecklistsState, allChecklistsOriginal, isEqual);
      const allActionsOriginal = originalWir?.actions ?? [];
      const allActionsState = wirFromState?.actions ?? [];
      const newActions = differenceWith(allActionsState, allActionsOriginal, isEqual);

      wirFromState?.wirNumber &&
        processSaveResponse(
          await wellGuardianApiClient.updateWIRChecksActions(wirFromState?.wirNumber, {
            phase,
            section,
            assignedTo,
            comments,
            sectionCompleteConfirmation,
            actions: newActions,
            checks: newChecks,
          }),
        );
    },
    [wirFromState, originalWir, processSaveResponse],
  );

  const getKomReviewCheckListActions = useCallback(async () => {
    const { phase: phaseKomOrRs } = getKomTechnicalWirReactiveScopeData(wirFromState, countryState);
    if (countryState === CountryData.denmark) {
      (section === WIRStageGate2Sections.reviewChecklistActions ||
        section === WIRReactiveScopeSections.reviewChecklistActions) &&
        (await saveChecklistAndActions(
          'REVIEW_CHECKLIST_ACTIONS',
          getIsUnplannedReactiveScope(wirFromState) ? 'REACTIVE_SCOPE' : 'STAGE_GATE_2',
          wirFromState?.phases?.[phaseKomOrRs]?.sections?.reviewChecklistActions?.assignedTo || '',
          wirFromState?.phases?.[phaseKomOrRs]?.sections?.reviewChecklistActions
            ?.sectionCompleteConfirmation,
          wirFromState?.phases?.[phaseKomOrRs]?.sections?.reviewChecklistActions?.comments || [],
        ));
    } else {
      (section === WIRKofMeetingTechnicalWirSections.reviewChecklistActions ||
        section === WIRReactiveScopeSections.reviewChecklistActions) &&
        (await saveChecklistAndActions(
          'REVIEW_CHECKLIST_ACTIONS',
          getIsUnplannedReactiveScope(wirFromState)
            ? 'REACTIVE_SCOPE'
            : getKickOffPhaseName(countryState),
          wirFromState?.phases?.[phaseKomOrRs]?.sections?.reviewChecklistActions?.assignedTo || '',
          wirFromState?.phases?.[phaseKomOrRs]?.sections?.reviewChecklistActions
            ?.sectionCompleteConfirmation,
          wirFromState?.phases?.[phaseKomOrRs]?.sections?.reviewChecklistActions?.comments || [],
        ));
    }
  }, [countryState, saveChecklistAndActions, section, wirFromState]);

  const getEnggWorkReviewCheckListActions = useCallback(async () => {
    // engineeringWorkSectionKey
    const engineeringWorkSectionKey = getEngineeringWorkSectionKey(countryState);
    const engineeringWorkPhase = getEngineeringWorkPhaseName(countryState);
    (section === WIRStageGate3Sections.reviewChecklistActions ||
      section === WIREngineeringWorkSections.reviewChecklistActions) &&
      (await saveChecklistAndActions(
        'REVIEW_CHECKLIST_ACTIONS',
        engineeringWorkPhase,
        wirFromState?.phases?.[engineeringWorkSectionKey]?.sections?.reviewChecklistActions
          ?.assignedTo || '',
        wirFromState?.phases?.[engineeringWorkSectionKey]?.sections?.reviewChecklistActions
          ?.sectionCompleteConfirmation,
        wirFromState?.phases?.[engineeringWorkSectionKey]?.sections?.reviewChecklistActions
          ?.comments || [],
      ));
  }, [countryState, saveChecklistAndActions, section, wirFromState]);

  const saveWir = useCallback(
    async (updatedPhase: Phase) => {
      if (wirFromState?.wirNumber) {
        setWirUpdateLoading(true);

        // use for common api - updateWIRGenericSection
        await processSaveWirResponse(section, processSaveResponse, updatedPhase);

        await saveInitialRequestInformation(section, processSaveResponse);
        await saveOrganiseKickOffMeetingSection(section, processSaveResponse);
        await saveEquipmentIdentificationSection(section, processSaveResponse);
        await saveWellInformationSection(section, processSaveResponse);
        await saveManagerialSummarySection(section, processSaveResponse);
        await saveCloseoutChecklistSection(section, processSaveResponse);

        getKomReviewCheckListActions();
        getEnggWorkReviewCheckListActions();

        setWirUpdateLoading(false);
      }
    },
    [
      wirFromState?.wirNumber,
      processSaveWirResponse,
      section,
      processSaveResponse,
      saveInitialRequestInformation,
      saveOrganiseKickOffMeetingSection,
      saveEquipmentIdentificationSection,
      saveWellInformationSection,
      saveManagerialSummarySection,
      getKomReviewCheckListActions,
      getEnggWorkReviewCheckListActions,
      saveCloseoutChecklistSection,
    ],
  );

  return {
    saveWir,
    wirUpdateLoading,
    setUnsavedChangesModalOpen,
    unsavedChangesModalOpen,
    cancelSaveChanges,
    processSaveResponse,
  };
};
